import Cookie from '../utils/Cookie';
import { store } from '../redux/store';
import { io } from 'socket.io-client';
import { setSocketState } from '../redux/slice/socketSlice';
import { obs, ObsEvent } from '../services/observability';


const API_URI = process.env.REACT_APP_API_URL;
let socket = null;
let connectionStartTime = null;

function getSocket() {
    return socket;
}

async function SocketConnect(token) {
    connectionStartTime = Date.now();

    socket = io(API_URI, {
        auth: {
            token: `${token}`,
        },
        transports: ['websocket'],
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
    });

    socket.on('connect', () => {
        const connectionMs = connectionStartTime ? Date.now() - connectionStartTime : null;
        console.log('Socket connected:', socket.id);

        obs.emit(ObsEvent.SOCKET_CONNECTED, {
            connection_ms: connectionMs,
            socket_id: socket.id,
            status: 'success'
        });
    });

    socket.on('reconnect', (attemptNumber) => {
        console.log('Socket reconnected after', attemptNumber, 'attempts');
        obs.emit(ObsEvent.SOCKET_RECONNECTED, {
            attempt_number: attemptNumber,
            status: 'success'
        });
    });

    socket.on('reconnect_attempt', (attemptNumber) => {
        console.log('Socket reconnection attempt:', attemptNumber);
    });

    socket.on('reconnect_error', (error) => {
        console.log('Socket reconnection error:', error?.message);
        store.dispatch(setSocketState({ user: null, isInitialized: 'failed' }));
        obs.emit(ObsEvent.SOCKET_CONNECTION_DROPPED, {
            error_message: error?.message,
            status: 'error'
        });
    });

    socket.on('connection_established', (data) => {
        // Set session context for all future observability events
        obs.setSession(data?.user_id, data?.user_name);

        store.dispatch(setSocketState({ user: data, isInitialized: 'connected' }));
    });

    socket.on('error', (error) => {
        console.log('socket error', error);
        const message = error?.message || '';

        // Token expired: reconnect with a fresh token and keep socket usable.
        if (message.includes("Token expired")) {
            const token = Cookie.getCookie('__session');
            socket?.disconnect();
            socket = null;
            SocketConnect(token);
            return;
        }

        // Video token failure has its own UI state used by VideoChat.
        if (message.includes("Failed to generate video token")) {
            store.dispatch(setSocketState({ user: null, isInitialized: 'video_token_faild' }));
            return;
        }

        // Only treat true auth/connection failures as fatal. Application-
        // level errors (e.g. "chat_id is required", "Failed to generate
        // message") should NOT flip the UI to 'failed' — that locks users
        // into the "Connection failed — Reload" overlay even though the
        // socket itself is perfectly healthy.
        const isFatalConnectionError =
            message.includes("Authentication") ||
            message.includes("Invalid token") ||
            message.includes("missing user ID");

        if (isFatalConnectionError) {
            store.dispatch(setSocketState({ user: null, isInitialized: 'failed' }));
            return;
        }

        console.warn('Non-fatal socket app error (ignored for UI state):', message);
    });

    socket.on('connect_timeout', (timeout) => {
        console.log('socket connect_timeout', timeout);
        store.dispatch(setSocketState({ user: null, isInitialized: 'timeout' }));
    });

    socket.on('connect_error', (error) => {
        console.log('socket connect_error', error);
        store.dispatch(setSocketState({ user: null, isInitialized: 'failed' }));
        obs.emit(ObsEvent.SOCKET_CONNECTION_DROPPED, {
            error_message: error?.message,
            error_type: 'connect_error',
            status: 'error'
        });
    });

    socket.on('disconnect', (reason) => {
        console.log('socket disconnect', reason);

        // 'io client disconnect' and 'io server disconnect' are intentional
        // Others like 'transport close', 'transport error', 'ping timeout' are unexpected
        const isExpected = reason === 'io client disconnect' || reason === 'io server disconnect';
        obs.emit(ObsEvent.SOCKET_DISCONNECTED, {
            reason: reason,
            status: isExpected ? 'info' : 'error'
        });

        store.dispatch(setSocketState({ user: null, isInitialized: 'disconnected' }));
        socket = null;
    });
}

async function SocketDisconnect() {
    if (socket) {
        socket?.disconnect();
        socket = null;
    }
}

export default {
    SocketConnect,
    SocketDisconnect,
    socket,
};

export { SocketConnect, SocketDisconnect, socket, getSocket };
